import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import configuration from "../../configuration";
import { trackingConst } from "../../constants";
import { Analytics } from "../../types";
import { useAnalytics } from "../../utils/analytics";
import { convertToSalesforceLocale } from "../../utils/convertToSalesforceLocale";
import { logToSentry } from "../../utils/helpers";
import * as Types from "./types";

const widgetId = configuration.salesforceWidgetId;
const widgetType = configuration.salesforceWidgetType;
const bootstrapUrl = configuration.salesforceBootstrapUrl;
const scrt2URL = configuration.salesforceScrt2URL;

const initEmbeddedMessaging = (
  customerName: string,
  customerEmail: string,
  websiteUrl: string,
  planName: string,
  domainId: string,
  salesforceId: string,
  locale: string,
  analytics: Analytics,
  page: string,
) => {
  const event = {
    name: trackingConst.events.helpWanted,
    properties: {
      context: "Business App",
      placement: "bottom right corner",
      action: "Toggles visibility of the Salesforce support center widget",
      name: "widget-support-help",
    },
  };
  window.addEventListener("onEmbeddedMessagingWindowMaximized", () => {
    try {
      analytics.track(event.name, { ...event.properties, page });
    } catch (e) {
      logToSentry(e);
    }
  });

  window.addEventListener("onEmbeddedMessagingReady", () => {
    try {
      // These fields have been defined by the salesfoce team and as a result the names, with their mismatch of camelCase and snake_case, are not to be changed.
      // The spreadsheet with the details can be found in this jira ticket: https://trustpilot-production.atlassian.net/browse/BWA-12916
      window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
        FirstName: customerName.substring(0, customerName.indexOf(" ")),
        LastName: customerName.substring(customerName.indexOf(" ") + 1),
        Email: customerEmail,
        WebsiteUrl: websiteUrl,
        Plan: planName,
        BusinessId: domainId,
        ID: salesforceId,
        Language: convertToSalesforceLocale(locale),
      });
    } catch (e) {
      logToSentry(e);
    }
  });
  window.embeddedservice_bootstrap.settings.language = convertToSalesforceLocale(locale);
  window.embeddedservice_bootstrap.init(widgetId, widgetType, bootstrapUrl, {
    scrt2URL,
  });
};

const initialiseSalesforceChat = (
  customerName: string,
  customerEmail: string,
  websiteUrl: string,
  planName: string,
  domainId: string,
  salesforceId: string,
  locale: string,
  analytics: Analytics,
  page: string,
) => {
  new Promise<void>((resolve, reject) => {
    const externalScript = document.createElement("script");
    externalScript.type = "text/javascript";
    externalScript.src = `${bootstrapUrl}/assets/js/bootstrap.min.js`;
    externalScript.onload = () => {
      initEmbeddedMessaging(
        customerName,
        customerEmail,
        websiteUrl,
        planName,
        domainId,
        salesforceId,
        locale,
        analytics,
        page,
      );

      resolve();
    };
    externalScript.onerror = reject;
    document.body.appendChild(externalScript);
  });
};

export const SalesforceChat: React.FC<Types.SalesforceChat> = ({
  businessUser,
  businessUnit,
  salesforceId,
  locale,
}) => {
  const analytics = useAnalytics();
  const { pathname } = useLocation();

  useEffect(() => {
    const { name, email } = businessUser;
    const { id, planName } = businessUnit;
    const websiteUrl = businessUnit.name?.identifying;

    if (name && email && websiteUrl && planName && id && salesforceId && locale) {
      try {
        initialiseSalesforceChat(name, email, websiteUrl, planName, id, salesforceId, locale, analytics, pathname);
      } catch (e) {
        logToSentry(e);
      }
    }
  }, []);

  return <React.Fragment />;
};
